import React, { useCallback, useEffect, useState } from 'react';
import { Painel } from '../../../../components/Painel';
import { Pagination } from '../../../../components/Pagination';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';
import { useModal } from '../../../../hooks/modal';
import { useToast } from '../../../../hooks/toast';
import { ModalPayments } from '../../../../modais/payments';
import { api } from '../../../../services/api';
import { useFeature } from '../../../../hooks/features';
import { downloadBlob } from '../../../../utils/downloadBlob';

import {
  ActionLink,
  ActionPlaceholder,
  Button,
  Container,
  HeaderActions,
  HistoryPaymentsContainer,
  Info,
  PageHeader,
  PageTitle,
  Separate,
  StatusCancelled,
  StatusPaid,
  StatusPending,
  WarningsContainer,
} from './styles';

interface IBillProps {
  id: string;
  gym_id: string;
  subscription_id: string;
  subscription_data_id: string;
  price: number;
  status: string;
  paid_at: string;
  due_at: string;
}

const PAGE_SIZE = 10;

const Payments: React.FC = () => {
  const [bills, setBills] = useState<IBillProps[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const hasExportData = useFeature('export_data');
  const [exporting, setExporting] = useState(false);

  const { addModal } = useModal();
  const { addToast } = useToast();

  const getBills = useCallback(
    (currentPage: number, notify?: boolean) => {
      setLoading(true);
      api
        .get('payments', { params: { page: currentPage, limit: PAGE_SIZE } })
        .then(response => {
          setBills(response.data.data);
          setTotal(response.data.total);
          setTotalPages(response.data.totalPages);
        })
        .catch(err => console.log(err?.response?.status))
        .finally(() => {
          setLoading(false);
        });

      if (notify) {
        addToast({
          type: 'info',
          title: 'Lista de pagamentos atualizada.',
        });
      }
    },
    [addToast],
  );

  const handleRefresh = useCallback(() => {
    setPage(1);
    getBills(1, true);
  }, [getBills]);

  useEffect(() => {
    getBills(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleExportHistory = useCallback(async () => {
    if (!hasExportData || exporting) return;

    setExporting(true);

    try {
      const response = await api.get('payments/export', {
        responseType: 'blob',
      });
      downloadBlob(response.data, 'historico-financeiro.csv');
    } catch (err) {
      console.log(err);
    } finally {
      setExporting(false);
    }
  }, [hasExportData, exporting]);

  return (
    <Painel loading={loading}>
      <Container>
        <PageHeader>
          <PageTitle>Histórico de pagamentos</PageTitle>
          <HeaderActions>
            <Button
              name="export"
              disabled={!hasExportData || exporting}
              value={
                exporting
                  ? 'Exportando...'
                  : !hasExportData
                  ? 'Exportar CSV — Performance'
                  : 'Exportar CSV'
              }
              onClick={handleExportHistory}
            />
            <Button name="refresh" value="Atualizar" onClick={handleRefresh} />
          </HeaderActions>
        </PageHeader>

        <WarningsContainer>
          <Info text="Nessa área você pode visualizar de forma simples e clara o resumo das suas faturas dos últimos 12 meses." />
          <Info text="Caso haja alguma fatura com status “Em aberto”, basta clicar no link em destaque para abrir os meios de pagamento." />
          <Info text="Ao realizar o pagamento, aguarde alguns instantes para confirmação abaixo." />
        </WarningsContainer>

        <HistoryPaymentsContainer>
          <Separate label="Informações gerais" />

          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>Data</Th>
                  <Th>Vencimento</Th>
                  <Th>Valor</Th>
                  <Th>Status</Th>
                  <Th>Ação</Th>
                </tr>
              </Thead>
              <Tbody>
                {bills.map(bill => {
                  const due_at = new Date(bill.due_at);
                  const month = `${
                    due_at.getMonth() + 1
                  }/${due_at.getFullYear()}`;

                  return (
                    <tr key={bill.id}>
                      <Td>{month}</Td>
                      <Td>{`${due_at.getDate()}/${
                        due_at.getMonth() + 1
                      }/${due_at.getFullYear()}`}</Td>
                      <Td>R$ {bill.price}</Td>
                      <Td>
                        {bill.status === 'approved' ? (
                          <StatusPaid>Paga</StatusPaid>
                        ) : bill.status === 'cancelled' ? (
                          <StatusCancelled>Cancelada</StatusCancelled>
                        ) : (
                          <StatusPending>Em aberto</StatusPending>
                        )}
                      </Td>
                      <Td>
                        {bill.status === 'waiting' ? (
                          <ActionLink
                            type="button"
                            onClick={() =>
                              addModal(
                                <ModalPayments
                                  bill_id={bill.id}
                                  month={month}
                                />,
                              )
                            }
                          >
                            Pagar agora
                          </ActionLink>
                        ) : (
                          <ActionPlaceholder>—</ActionPlaceholder>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </Tbody>
            </Table>

            {!bills.length && (
              <EmptyState>Nenhuma fatura encontrada.</EmptyState>
            )}
          </TableWrapper>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            itemLabel="faturas"
            onPageChange={setPage}
          />
        </HistoryPaymentsContainer>
      </Container>
    </Painel>
  );
};

export { Payments };
